import { NgOptimizedImage } from '@angular/common';
import { booleanAttribute, Component, computed, input, signal } from '@angular/core';

import { MibIconComponent } from '../icons';
import { MibSpinnerComponent } from '../loaders';
import { MibTooltipDirective } from '../tooltip';
import { THUMBNAIL_SIZE } from './thumbnail.config';
import {
  MibThumbnailBusyIcon,
  MibThumbnailLabelMode,
  MibThumbnailSize,
  MibThumbnailUnavailable,
} from './thumbnail.models';

/**
 * The picture of a case: its still covering the frame, or a plain ground when there is none.
 * A case card draws it, and an app writes it in a table cell.
 *
 * ```html
 * <mib-thumbnail [src]="c.thumbnail" [unavailable]="c.unavailable" [busy]="c.statusText" />
 * ```
 */
@Component({
  selector: 'mib-thumbnail',
  imports: [MibIconComponent, MibSpinnerComponent, MibTooltipDirective, NgOptimizedImage],
  templateUrl: './thumbnail.component.html',
  host: {
    class: 'block overflow-hidden',
    '[class]': 'frameClasses()',
    '[class.bg-gray-900]': '!unavailable()',
    '[class.bg-mib-branding-900]': '!!unavailable()',
  },
})
export class MibThumbnailComponent {
  /** The still to draw, covering the frame. Absent or failing to load, the plain ground is drawn. */
  readonly src = input<string>();

  /** Loads the still first, for a picture seen before scrolling: set it on the first row only. */
  readonly priority = input(false, { transform: booleanAttribute });

  /** `sm` is a 64px-wide 16:9 frame for a table cell; `fill` covers the nearest positioned container. */
  readonly size = input<MibThumbnailSize>('sm');

  /** The picture is not available here: its icon on the branding ground, and no still. */
  readonly unavailable = input<MibThumbnailUnavailable | null>();

  /** How the label of {@link unavailable} is given: as the icon's tooltip, or written under it. */
  readonly labelMode = input<MibThumbnailLabelMode>('tooltip');

  /** Something is being done to the picture: a mark over it, named by this sentence. */
  readonly busy = input<string | null>();

  /** The mark {@link busy} draws; `null` takes the size's own: `refresh` at `sm`, `spinner` at `fill`. */
  readonly busyIcon = input<MibThumbnailBusyIcon | null>(null);

  /** Shows the spinner until the still has loaded, for a still that is slow to come. */
  readonly loader = input(false, { transform: booleanAttribute });

  /** A URL that failed to load: drawn as no still until another is handed over. */
  protected readonly failedSrc = signal<string | null>(null);

  /** The last URL that finished loading, so a new one brings the loader back. */
  protected readonly loadedSrc = signal<string | null>(null);

  protected readonly source = computed(() => {
    const src = this.src();
    return src === this.failedSrc() ? undefined : src;
  });

  protected readonly loading = computed(
    () => this.loader() && !!this.source() && this.loadedSrc() !== this.source() && !this.unavailable() && !this.busy(),
  );

  protected readonly metrics = computed(() => THUMBNAIL_SIZE[this.size()]);

  protected readonly busyMark = computed(() => this.busyIcon() ?? this.metrics().busyIcon);

  protected readonly frameClasses = computed(() => this.metrics().frame);

  protected readonly iconClasses = computed(() => `${this.metrics().icon} text-white`);

  protected readonly busyIconClasses = computed(() => `${this.iconClasses()} animate-spin motion-reduce:animate-none`);
}
